import React, { useCallback, useState } from 'react';
import { PriceField } from 'components/UI/V3/Forms/PriceField/PriceField';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import styles from './TransportationPriceFields.module.scss';

interface TransportationPricesType {
    pickUpPrice: number;
    dropOffPrice: number;
}

interface TransportationPriceFieldsProps {
    prices: TransportationPricesType;
    onPricesChange: (prices: TransportationPricesType) => void;
    onErrorsChange?: (errors: { pickUp?: string; dropOff?: string }) => void;
}

export const TransportationPriceFields: React.FC<TransportationPriceFieldsProps> = ({
    prices,
    onPricesChange,
    onErrorsChange
}) => {
    const [errors, setErrors] = useState<{ pickUp?: string; dropOff?: string }>({});
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

    const handlePriceChange = useCallback(
        (field: 'pickUpPrice' | 'dropOffPrice', value: number | undefined) => {
            onPricesChange({
                ...prices,
                [field]: value || 0
            });

            // Clear error if field is being edited
            const errorKey = field === 'pickUpPrice' ? 'pickUp' : 'dropOff';
            if (errors[errorKey]) {
                const newErrors = { ...errors };
                delete newErrors[errorKey];
                setErrors(newErrors);
                onErrorsChange?.(newErrors);
            }
        },
        [prices, errors, onPricesChange, onErrorsChange]
    );

    const handleBlur = useCallback(
        (field: 'pickUpPrice' | 'dropOffPrice') => {
            const errorKey = field === 'pickUpPrice' ? 'pickUp' : 'dropOff';
            setTouchedFields(prev => new Set(prev).add(errorKey));

            const value = prices[field];
            const newErrors = { ...errors };

            if (value < 0) {
                newErrors[errorKey] = 'Price must be positive';
            } else {
                delete newErrors[errorKey];
            }

            setErrors(newErrors);
            onErrorsChange?.(newErrors);
        },
        [prices, errors, onErrorsChange]
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Label>Transportation Prices</Label>
                <DescriptionText text="Set fixed prices for pick up and drop off services. Both prices are required. Round trip price will be calculated automatically as the sum of both prices." />
            </div>

            <div className={styles.priceFields}>
                {/* Pick Up Price */}
                <div className={styles.priceField}>
                    <Label className={styles.label}>Pick Up Price</Label>
                    <div className={styles.inputWrapper}>
                        <PriceField
                            scale={2}
                            value={prices.pickUpPrice}
                            onChange={value => handlePriceChange('pickUpPrice', value)}
                            onBlur={() => handleBlur('pickUpPrice')}
                            placeholder="0.00"
                            className={styles.priceInput}
                        />
                    </div>
                    {touchedFields.has('pickUp') && errors.pickUp && (
                        <div className={styles.error}>{errors.pickUp}</div>
                    )}
                </div>

                {/* Drop Off Price */}
                <div className={styles.priceField}>
                    <Label className={styles.label}>Drop Off Price</Label>
                    <div className={styles.inputWrapper}>
                        <PriceField
                            scale={2}
                            value={prices.dropOffPrice}
                            onChange={value => handlePriceChange('dropOffPrice', value)}
                            onBlur={() => handleBlur('dropOffPrice')}
                            placeholder="0.00"
                            className={styles.priceInput}
                        />
                    </div>
                    {touchedFields.has('dropOff') && errors.dropOff && (
                        <div className={styles.error}>{errors.dropOff}</div>
                    )}
                </div>
            </div>

            <div
                style={{ display: 'none' }}
                className={styles.infoBox}
            >
                <p>
                    <strong>Note:</strong> When creating an appointment, the customer will choose between Pick Up, Drop
                    Off, or Round Trip. Round Trip price will be automatically calculated as Pick Up Price + Drop Off
                    Price.
                </p>
            </div>
        </div>
    );
};
